<template>
  <div class="w-full" @click="handleRowClick">
    <!-- Parent row -->
    <div
      class="flex gap-2 items-start"
      :class="[
        computedShowChildrenDates && !alwaysShowMultidate ? 'mb-1.5' : '',
        isSelected && !alwaysShowMultidate
          ? 'bg-teal-50 selected-parent-session'
          : '',
      ]"
    >
      <div
        class="flex flex-col items-center self-stretch w-4 flex-shrink-0"
        :class="[!alwaysShowMultidate ? '' : 'hidden']"
      >
        <template v-if="!alwaysShowMultidate">
          <Check
            v-if="isSelected"
            class="h-3 w-3 p-[2px] mr-1 mt-1 rounded-full flex-shrink-0 text-white bg-teal-600 z-10"
          />
          <div
            v-else
            class="w-3 h-3 rounded-full bg-teal-200 flex-shrink-0 mt-1.5 -ml-1"
          ></div>
        </template>
        <div
          v-else
          class="w-3 h-3 rounded-full bg-teal-200 border border-teal-300 flex-shrink-0 mt-1 z-10"
        ></div>
      </div>
      <div class="flex justify-between items-center flex-1 min-w-0 pb-0.5">
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <div v-html="fnGetDateLabelHtml(optionData)"></div>
          <EventBuyBoxTimeSlotsBadge
            class="ml-1"
            :date="optionData as EventDate"
          />
        </div>
        <span
          v-if="showCollapseButton && !alwaysShowMultidate && hasChildren"
          class="text-teal-400 bg-teal-50 flex-shrink-0 ml-2 rounded-full p-1"
        >
          <Plus v-if="!multiDateIsOpen" class="h-3 w-3" />
          <Minus v-else class="h-3 w-3" />
        </span>
      </div>
    </div>

    <!-- Children rows -->
    <div
      v-if="computedShowChildrenDates && visibleChildren.length > 0"
      class="date-option-children"
    >
      <div
        v-for="child in visibleChildren"
        :key="child.id"
        class="flex gap-2 items-start py-1.5"
      >
        <div
          class="w-2 h-2 rounded-full bg-teal-200 flex-shrink-0 mt-1 ml-[1px]"
        ></div>
        <div
          class="text-sm text-gray-600 flex-1"
          v-html="fnGetDateLabelHtml(child)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Check, Plus, Minus } from 'lucide-vue-next'
import type { EventDate, EventDateChildren } from '~/composables/types/event'

defineOptions({ name: 'EventBuyBoxDateOption' })

const props = defineProps<{
  optionData: EventDate | EventDateChildren
  alwaysShowMultidate?: boolean
  forceShowMultidate?: boolean
  showCollapseButton?: boolean
  maxChildren?: number
}>()

const { fnGetDateLabelHtml } = useEventDateHelpers()

const openDateId = inject<Ref<string | undefined>>(
  'multidateOpenDateId',
  ref(undefined)
)
const setOpenDateId = inject<(id: string | undefined) => void>(
  'setMultidateOpenDateId',
  () => {}
)
const injectedSelectedDate = inject<Ref<EventDate | undefined>>(
  'multidateSelectedDate',
  ref(undefined)
)
const setSelectedDate = inject<(date: EventDate) => void>(
  'setMultidateSelectedDate',
  () => {}
)

const isSelected = computed(
  () => injectedSelectedDate.value?.id === props.optionData.id
)
const multiDateIsOpen = ref(false)

watch(
  openDateId,
  (id) => {
    if (!props.showCollapseButton) return
    multiDateIsOpen.value = id === props.optionData.id
  },
  { immediate: true }
)

const handleRowClick = (event: MouseEvent) => {
  if (
    !props.showCollapseButton ||
    props.alwaysShowMultidate ||
    !hasChildren.value
  )
    return
  if (!isSelected.value) {
    // first click: highlight + open children, keep dropdown open
    event.stopPropagation()
    setOpenDateId(props.optionData.id)
    setSelectedDate(props.optionData as EventDate)
  }
  // already selected: let propagate → Select closes the dropdown
}

const children = computed(() => eventDateGetters.getChildren(props.optionData))
const hasChildren = computed(() => children.value.length > 0)
const isSoldOut = computed(() => props.optionData.status === 'SOLD_OUT')

const visibleChildren = computed(() =>
  props.maxChildren !== undefined
    ? children.value.slice(0, props.maxChildren)
    : children.value
)

const computedShowChildrenDates = computed(
  () =>
    (!isSoldOut.value &&
      (props.alwaysShowMultidate || props.forceShowMultidate) &&
      hasChildren.value) ||
    multiDateIsOpen.value
)
</script>
<style scoped lang="postcss">
.date-option-children {
  @apply relative;
}
.date-option-children::before {
  content: '';
  @apply absolute bg-teal-200 w-[1px] h-full;
  left: 5px;
  top: -16px;
}

.selected-parent-session {
  @apply items-center;
  box-shadow:
    -12px -8px 0 0 theme('colors.teal.50'),
    12px -8px 0 0 theme('colors.teal.50'),
    -12px 6px 0 0 theme('colors.teal.50'),
    12px 6px 0 0 theme('colors.teal.50');
}
</style>
